import { Dispatch, SetStateAction } from "react";
import {
  IAddress,
  ICommitteeMemberInput,
  ICooperativeDetails,
  IDeclaration,
  IDocumentsInput,
  IExecutiveMemberInput,
  IFinancialAuditInput,
  IFinancialIncome,
  IFinancialLoan,
  IFinancialShare,
  IFinancialTax,
  IShareMemberInput,
} from "../../exports/interfaces";
import { base64ToFile, fileToBase64 } from "../../exports/functions";

export const serializeFile = async (
  file: any,
): Promise<{ base64: string; name: string; type: string } | null> => {
  if (!file) return null;
  if (file instanceof File) {
    try {
      return await fileToBase64(file);
    } catch {
      return null;
    }
  }
  if (typeof file === "object" && file.base64 && file.name) {
    return {
      base64: file.base64,
      name: file.name,
      type: file.type || "",
    };
  }
  return null;
};

export const safeBase64ToFile = async (fileObj: any): Promise<File | null> => {
  if (!fileObj || !fileObj.base64) return null;
  try {
    return await base64ToFile(
      fileObj.base64,
      fileObj.name || "file",
      fileObj.type || "",
    );
  } catch (err) {
    console.error("Failed to convert base64 to File:", err);
    return null;
  }
};

export const saveCooperativeDraft = (cooperativeInfo: ICooperativeDetails) => {
  try {
    localStorage.setItem(
      "cooperative",
      JSON.stringify({
        formData: {
          ...cooperativeInfo,
        },
      }),
    );
  } catch (err) {
    console.error("Failed to save cooperative draft:", err);
  }
};

export const saveAddressDraft = (address: IAddress) => {
  try {
    localStorage.setItem(
      "address",
      JSON.stringify({
        formData: {
          ...address,
        },
      }),
    );
  } catch (err) {
    console.error("Failed to save address draft:", err);
  }
};

export const saveShareFinancialDraft = (shareFinancial: IFinancialShare) => {
  try {
    localStorage.setItem(
      "shareFinancial",
      JSON.stringify({
        formData: {
          ...shareFinancial,
        },
      }),
    );
  } catch (err) {
    console.error("Failed to save shareFinancial draft:", err);
  }
};

export const saveLoanFinancialDraft = (loanFinancial: IFinancialLoan) => {
  try {
    localStorage.setItem(
      "loanFinancial",
      JSON.stringify({
        formData: {
          ...loanFinancial,
        },
      }),
    );
  } catch (err) {
    console.error("Failed to save loanFinancial draft:", err);
  }
};

export const saveIncomeFinancialDraft = (incomeFinancial: IFinancialIncome) => {
  try {
    localStorage.setItem(
      "incomeFinancial",
      JSON.stringify({
        formData: {
          ...incomeFinancial,
        },
      }),
    );
  } catch (err) {
    console.error("Failed to save incomeFinancial draft:", err);
  }
};

export const saveAuditFinancialDraft = async (
  auditFinancial: IFinancialAuditInput,
) => {
  try {
    const file = await serializeFile(auditFinancial.file);
    localStorage.setItem(
      "auditFinancial",
      JSON.stringify({
        formData: {
          ...auditFinancial,
          file,
        },
      }),
    );
  } catch (err) {
    console.error("Failed to save auditFinancial draft:", err);
  }
};

export const saveTaxFinancialDraft = (taxFinancial: IFinancialTax) => {
  try {
    localStorage.setItem(
      "taxFinancial",
      JSON.stringify({
        formData: {
          ...taxFinancial,
        },
      }),
    );
  } catch (err) {
    console.error("Failed to save taxFinancial draft:", err);
  }
};

export const saveCommitteeCooperativeDraft = async (
  committeeCooperative: ICommitteeMemberInput,
) => {
  try {
    const [file, bodFile, citizenshipFront, citizenshipBack] =
      await Promise.all([
        serializeFile(committeeCooperative.file),
        serializeFile(committeeCooperative.bodFile),
        serializeFile(committeeCooperative.citizenshipFront),
        serializeFile(committeeCooperative.citizenshipBack),
      ]);

    localStorage.setItem(
      "committeeCooperative",
      JSON.stringify({
        formData: {
          ...committeeCooperative,
          file,
          bodFile,
          citizenshipFront,
          citizenshipBack,
        },
      }),
    );
  } catch (err) {
    console.error("Failed to save committeeCooperative draft:", err);
  }
};

export const saveExecutiveCooperativeDraft = async (
  executiveCooperative: IExecutiveMemberInput,
) => {
  try {
    const [file, citizenshipFront, citizenshipBack] = await Promise.all([
      serializeFile(executiveCooperative.file),
      serializeFile(executiveCooperative.citizenshipFront),
      serializeFile(executiveCooperative.citizenshipBack),
    ]);

    localStorage.setItem(
      "executiveCooperative",
      JSON.stringify({
        formData: {
          ...executiveCooperative,
          file,
          citizenshipFront,
          citizenshipBack,
        },
      }),
    );
  } catch (err) {
    console.error("Failed to save executiveCooperative draft:", err);
  }
};

export const saveCommitteeTableDraft = async (
  committeeTableCooperative: ICommitteeMemberInput[],
) => {
  try {
    const serializedMembers = await Promise.all(
      committeeTableCooperative.map(async (member) => ({
        ...member,
        file: await serializeFile(member.file),
        bodFile: await serializeFile(member.bodFile),
        citizenshipFront: await serializeFile(member.citizenshipFront),
        citizenshipBack: await serializeFile(member.citizenshipBack),
      })),
    );

    localStorage.setItem(
      "committeeTableCooperative",
      JSON.stringify({
        formData: serializedMembers,
      }),
    );
  } catch (err) {
    console.error("Failed to save committeeTableCooperative draft:", err);
  }
};

export const saveExecutiveTableDraft = async (
  executiveTableCooperative: IExecutiveMemberInput[],
) => {
  try {
    const serializedMembers = await Promise.all(
      executiveTableCooperative.map(async (member) => ({
        ...member,
        file: await serializeFile(member.file),
        citizenshipFront: await serializeFile(member.citizenshipFront),
        citizenshipBack: await serializeFile(member.citizenshipBack),
      })),
    );

    localStorage.setItem(
      "executiveTableCooperative",
      JSON.stringify({
        formData: serializedMembers,
      }),
    );
  } catch (err) {
    console.error("Failed to save executiveTableCooperative draft:", err);
  }
};

export const saveShareMemberDraft = async (
  shareMember: IShareMemberInput,
) => {
  try {
    const [shareMemberFile, savingMemberFile, loanMemberFile] =
      await Promise.all([
        serializeFile(shareMember.shareMemberFile),
        serializeFile(shareMember.savingMemberFile),
        serializeFile(shareMember.loanMemberFile),
      ]);

    localStorage.setItem(
      "shareMember",
      JSON.stringify({
        formData: {
          ...shareMember,
          shareMemberFile,
          savingMemberFile,
          loanMemberFile,
        },
      }),
    );
  } catch (err) {
    console.error("Failed to save shareMember draft:", err);
  }
};

export const saveDocumentsDraft = async (documents: IDocumentsInput) => {
  try {
    const rawOtherFiles =
      documents.otherDocumentFiles && documents.otherDocumentFiles.length > 0
        ? documents.otherDocumentFiles
        : documents.otherDocumentFile
        ? [documents.otherDocumentFile]
        : [];

    const [
      regCertificateFile,
      lawDocumentFile,
      foundingDocumentFile,
      panDocumentFile,
      meetingDocumentFile,
      businessRegistrationFile,
      taxClearanceFile,
      agmDocumentFile,
      otherDocumentFiles,
    ] = await Promise.all([
      serializeFile(documents.regCertificateFile),
      serializeFile(documents.lawDocumentFile),
      serializeFile(documents.foundingDocumentFile),
      serializeFile(documents.panDocumentFile),
      serializeFile(documents.meetingDocumentFile),
      serializeFile(documents.businessRegistrationFile),
      serializeFile(documents.taxClearanceFile),
      serializeFile(documents.agmDocumentFile),
      Promise.all(rawOtherFiles.map((f) => serializeFile(f))),
    ]);

    localStorage.setItem(
      "documents",
      JSON.stringify({
        formData: {
          ...documents,
          regCertificateFile,
          lawDocumentFile,
          foundingDocumentFile,
          panDocumentFile,
          meetingDocumentFile,
          businessRegistrationFile,
          taxClearanceFile,
          agmDocumentFile,
          otherDocumentFiles,
          otherDocumentFile: otherDocumentFiles[0] || null,
        },
      }),
    );
  } catch (err) {
    console.error("Failed to save documents draft:", err);
  }
};

export const saveDeclarationDraft = (declaration: IDeclaration) => {
  try {
    localStorage.setItem(
      "declaration",
      JSON.stringify({
        formData: {
          ...declaration,
        },
      }),
    );
  } catch (err) {
    console.error("Failed to save declaration draft:", err);
  }
};

export const saveToLocalStorage = async (
  currentStep: number,
  cooperativeInfo: ICooperativeDetails,
  address: IAddress,
  shareFinancial: IFinancialShare,
  loanFinancial: IFinancialLoan,
  incomeFinancial: IFinancialIncome,
  auditFinancial: IFinancialAuditInput,
  taxFinancial: IFinancialTax,
  committeeCooperative: ICommitteeMemberInput,
  executiveCooperative: IExecutiveMemberInput,
  committeeTableCooperative: ICommitteeMemberInput[],
  executiveTableCooperative: IExecutiveMemberInput[],
  shareMember: IShareMemberInput,
  declaration: IDeclaration,
  documents: IDocumentsInput,
) => {
  if (currentStep === 0) {
    saveCooperativeDraft(cooperativeInfo);
  } else if (currentStep === 1) {
    saveAddressDraft(address);
  } else if (currentStep === 2) {
    saveShareFinancialDraft(shareFinancial);
    saveLoanFinancialDraft(loanFinancial);
    saveIncomeFinancialDraft(incomeFinancial);
    await saveAuditFinancialDraft(auditFinancial);
    saveTaxFinancialDraft(taxFinancial);
  } else if (currentStep === 3) {
    await Promise.all([
      saveCommitteeCooperativeDraft(committeeCooperative),
      saveExecutiveCooperativeDraft(executiveCooperative),
      saveCommitteeTableDraft(committeeTableCooperative),
      saveExecutiveTableDraft(executiveTableCooperative),
    ]);
  } else if (currentStep === 4) {
    await saveShareMemberDraft(shareMember);
  } else if (currentStep === 5) {
    await saveDocumentsDraft(documents);
  } else if (currentStep === 6) {
    saveDeclarationDraft(declaration);
  }
};

export const loadRegisterData = async (
  currentStep: number,
  setCooperativeInfo: Dispatch<SetStateAction<ICooperativeDetails>>,
  setAddress: Dispatch<SetStateAction<IAddress>>,
  setShareFinancial: Dispatch<SetStateAction<IFinancialShare>>,
  setLoanFinancial: Dispatch<SetStateAction<IFinancialLoan>>,
  setIncomeFinancial: Dispatch<SetStateAction<IFinancialIncome>>,
  setAuditFinancial: Dispatch<SetStateAction<IFinancialAuditInput>>,
  setTaxFinancial: Dispatch<SetStateAction<IFinancialTax>>,
  setCommitteeCooperative: Dispatch<SetStateAction<ICommitteeMemberInput>>,
  setExecutiveCooperative: Dispatch<SetStateAction<IExecutiveMemberInput>>,
  setCommitteeTableCooperative: Dispatch<SetStateAction<ICommitteeMemberInput[]>>,
  setExecutiveTableCooperative: Dispatch<SetStateAction<IExecutiveMemberInput[]>>,
  setShareMember: Dispatch<SetStateAction<IShareMemberInput>>,
  setDeclaration: Dispatch<SetStateAction<IDeclaration>>,
  setDocuments: Dispatch<SetStateAction<IDocumentsInput>>,
) => {
  if (currentStep === 0) {
    const savedData = localStorage.getItem("cooperative");
    if (!savedData) return;
    try {
      const parsedData = JSON.parse(savedData);
      setCooperativeInfo({ ...parsedData.formData });
    } catch { /* ignore */ }
  } else if (currentStep === 1) {
    const savedData = localStorage.getItem("address");
    if (!savedData) return;
    try {
      const parsedData = JSON.parse(savedData);
      setAddress({ ...parsedData.formData });
    } catch { /* ignore */ }
  } else if (currentStep === 2) {
    const savedShareData = localStorage.getItem("shareFinancial");
    const savedLoanData = localStorage.getItem("loanFinancial");
    const savedIncomeData = localStorage.getItem("incomeFinancial");
    const savedAuditData = localStorage.getItem("auditFinancial");
    const savedTaxData = localStorage.getItem("taxFinancial");

    if (savedShareData) {
      try {
        const parsed = JSON.parse(savedShareData);
        setShareFinancial({ ...parsed.formData });
      } catch { /* ignore */ }
    }
    if (savedLoanData) {
      try {
        const parsed = JSON.parse(savedLoanData);
        setLoanFinancial({ ...parsed.formData });
      } catch { /* ignore */ }
    }
    if (savedIncomeData) {
      try {
        const parsed = JSON.parse(savedIncomeData);
        setIncomeFinancial({ ...parsed.formData });
      } catch { /* ignore */ }
    }
    if (savedAuditData) {
      try {
        const parsed = JSON.parse(savedAuditData);
        setAuditFinancial({
          ...parsed.formData,
          file: await safeBase64ToFile(parsed.formData?.file),
        });
      } catch { /* ignore */ }
    }
    if (savedTaxData) {
      try {
        const parsed = JSON.parse(savedTaxData);
        setTaxFinancial({ ...parsed.formData });
      } catch { /* ignore */ }
    }
  } else if (currentStep === 3) {
    const savedCommitteeData = localStorage.getItem("committeeCooperative");
    const savedExecutiveData = localStorage.getItem("executiveCooperative");
    const savedTableCommitteeData = localStorage.getItem("committeeTableCooperative");
    const savedTableExecutiveData = localStorage.getItem("executiveTableCooperative");

    if (savedCommitteeData) {
      try {
        const parsed = JSON.parse(savedCommitteeData);
        setCommitteeCooperative({
          ...parsed.formData,
          file: await safeBase64ToFile(parsed.formData?.file),
          bodFile: await safeBase64ToFile(parsed.formData?.bodFile),
          citizenshipFront: await safeBase64ToFile(parsed.formData?.citizenshipFront),
          citizenshipBack: await safeBase64ToFile(parsed.formData?.citizenshipBack),
        });
      } catch { /* ignore */ }
    }

    if (savedExecutiveData) {
      try {
        const parsed = JSON.parse(savedExecutiveData);
        setExecutiveCooperative({
          ...parsed.formData,
          file: await safeBase64ToFile(parsed.formData?.file),
          citizenshipFront: await safeBase64ToFile(parsed.formData?.citizenshipFront),
          citizenshipBack: await safeBase64ToFile(parsed.formData?.citizenshipBack),
        });
      } catch { /* ignore */ }
    }

    if (savedTableCommitteeData) {
      try {
        const parsed = JSON.parse(savedTableCommitteeData);
        const committeeData = await Promise.all(
          (parsed.formData || []).map(async (member: any) => ({
            ...member,
            file: await safeBase64ToFile(member.file),
            bodFile: await safeBase64ToFile(member.bodFile),
            citizenshipFront: await safeBase64ToFile(member.citizenshipFront),
            citizenshipBack: await safeBase64ToFile(member.citizenshipBack),
          })),
        );
        setCommitteeTableCooperative(committeeData);
      } catch { /* ignore */ }
    }

    if (savedTableExecutiveData) {
      try {
        const parsed = JSON.parse(savedTableExecutiveData);
        const executiveData = await Promise.all(
          (parsed.formData || []).map(async (member: any) => ({
            ...member,
            file: await safeBase64ToFile(member.file),
            citizenshipFront: await safeBase64ToFile(member.citizenshipFront),
            citizenshipBack: await safeBase64ToFile(member.citizenshipBack),
          })),
        );
        setExecutiveTableCooperative(executiveData);
      } catch { /* ignore */ }
    }
  } else if (currentStep === 4) {
    const savedShareMemberData = localStorage.getItem("shareMember");
    if (savedShareMemberData) {
      try {
        const parsed = JSON.parse(savedShareMemberData);
        setShareMember({
          ...parsed.formData,
          shareMemberFile: await safeBase64ToFile(parsed.formData?.shareMemberFile),
          savingMemberFile: await safeBase64ToFile(parsed.formData?.savingMemberFile),
          loanMemberFile: await safeBase64ToFile(parsed.formData?.loanMemberFile),
        });
      } catch { /* ignore */ }
    }
  } else if (currentStep === 5) {
    const savedDocumentsData = localStorage.getItem("documents");
    if (savedDocumentsData) {
      try {
        const parsed = JSON.parse(savedDocumentsData);
        const serializedOthers =
          parsed.formData?.otherDocumentFiles ||
          (parsed.formData?.otherDocumentFile
            ? [parsed.formData?.otherDocumentFile]
            : []);
        const loadedOtherFiles: File[] = [];
        for (const item of serializedOthers) {
          const f = await safeBase64ToFile(item);
          if (f) loadedOtherFiles.push(f);
        }

        setDocuments({
          ...parsed.formData,
          regCertificateFile: await safeBase64ToFile(parsed.formData?.regCertificateFile),
          lawDocumentFile: await safeBase64ToFile(parsed.formData?.lawDocumentFile),
          foundingDocumentFile: await safeBase64ToFile(parsed.formData?.foundingDocumentFile),
          panDocumentFile: await safeBase64ToFile(parsed.formData?.panDocumentFile),
          meetingDocumentFile: await safeBase64ToFile(parsed.formData?.meetingDocumentFile),
          businessRegistrationFile: await safeBase64ToFile(parsed.formData?.businessRegistrationFile),
          taxClearanceFile: await safeBase64ToFile(parsed.formData?.taxClearanceFile),
          agmDocumentFile: await safeBase64ToFile(parsed.formData?.agmDocumentFile),
          otherDocumentFiles: loadedOtherFiles,
          otherDocumentFileNames: loadedOtherFiles.map((f) => f.name),
          otherDocumentFile: loadedOtherFiles[0] || null,
          otherDocumentFileName: loadedOtherFiles[0]?.name || "",
        });
      } catch { /* ignore */ }
    }
  } else if (currentStep === 6) {
    const savedDeclarationData = localStorage.getItem("declaration");
    if (savedDeclarationData) {
      try {
        const parsed = JSON.parse(savedDeclarationData);
        setDeclaration({ ...parsed.formData });
      } catch { /* ignore */ }
    }
  }
};

export const loadAllRegisterData = async (
  setCooperativeInfo: Dispatch<SetStateAction<ICooperativeDetails>>,
  setAddress: Dispatch<SetStateAction<IAddress>>,
  setShareFinancial: Dispatch<SetStateAction<IFinancialShare>>,
  setLoanFinancial: Dispatch<SetStateAction<IFinancialLoan>>,
  setIncomeFinancial: Dispatch<SetStateAction<IFinancialIncome>>,
  setAuditFinancial: Dispatch<SetStateAction<IFinancialAuditInput>>,
  setTaxFinancial: Dispatch<SetStateAction<IFinancialTax>>,
  setCommitteeCooperative: Dispatch<SetStateAction<ICommitteeMemberInput>>,
  setExecutiveCooperative: Dispatch<SetStateAction<IExecutiveMemberInput>>,
  setCommitteeTableCooperative: Dispatch<SetStateAction<ICommitteeMemberInput[]>>,
  setExecutiveTableCooperative: Dispatch<SetStateAction<IExecutiveMemberInput[]>>,
  setShareMember: Dispatch<SetStateAction<IShareMemberInput>>,
  setDeclaration: Dispatch<SetStateAction<IDeclaration>>,
  setDocuments: Dispatch<SetStateAction<IDocumentsInput>>,
) => {
  // Step 0 – Cooperative Info
  const savedCooperative = localStorage.getItem("cooperative");
  if (savedCooperative) {
    try {
      const { formData } = JSON.parse(savedCooperative);
      setCooperativeInfo({ ...formData });
    } catch { /* ignore */ }
  }

  // Step 1 – Address
  const savedAddress = localStorage.getItem("address");
  if (savedAddress) {
    try {
      const { formData } = JSON.parse(savedAddress);
      setAddress({ ...formData });
    } catch { /* ignore */ }
  }

  // Step 2 – Financials
  const savedShareData = localStorage.getItem("shareFinancial");
  if (savedShareData) {
    try {
      const { formData } = JSON.parse(savedShareData);
      setShareFinancial({ ...formData });
    } catch { /* ignore */ }
  }

  const savedLoanData = localStorage.getItem("loanFinancial");
  if (savedLoanData) {
    try {
      const { formData } = JSON.parse(savedLoanData);
      setLoanFinancial({ ...formData });
    } catch { /* ignore */ }
  }

  const savedIncomeData = localStorage.getItem("incomeFinancial");
  if (savedIncomeData) {
    try {
      const { formData } = JSON.parse(savedIncomeData);
      setIncomeFinancial({ ...formData });
    } catch { /* ignore */ }
  }

  const savedAuditData = localStorage.getItem("auditFinancial");
  if (savedAuditData) {
    try {
      const { formData } = JSON.parse(savedAuditData);
      setAuditFinancial({
        ...formData,
        file: await safeBase64ToFile(formData?.file),
      });
    } catch { /* ignore */ }
  }

  const savedTaxData = localStorage.getItem("taxFinancial");
  if (savedTaxData) {
    try {
      const { formData } = JSON.parse(savedTaxData);
      setTaxFinancial({ ...formData });
    } catch { /* ignore */ }
  }

  // Step 3 – Committee & Executive
  const savedCommitteeData = localStorage.getItem("committeeCooperative");
  if (savedCommitteeData) {
    try {
      const { formData } = JSON.parse(savedCommitteeData);
      setCommitteeCooperative({
        ...formData,
        file: await safeBase64ToFile(formData?.file),
        bodFile: await safeBase64ToFile(formData?.bodFile),
        citizenshipFront: await safeBase64ToFile(formData?.citizenshipFront),
        citizenshipBack: await safeBase64ToFile(formData?.citizenshipBack),
      });
    } catch { /* ignore */ }
  }

  const savedExecutiveData = localStorage.getItem("executiveCooperative");
  if (savedExecutiveData) {
    try {
      const { formData } = JSON.parse(savedExecutiveData);
      setExecutiveCooperative({
        ...formData,
        file: await safeBase64ToFile(formData?.file),
        citizenshipFront: await safeBase64ToFile(formData?.citizenshipFront),
        citizenshipBack: await safeBase64ToFile(formData?.citizenshipBack),
      });
    } catch { /* ignore */ }
  }

  const savedTableCommitteeData = localStorage.getItem("committeeTableCooperative");
  if (savedTableCommitteeData) {
    try {
      const { formData } = JSON.parse(savedTableCommitteeData);
      const committeeData = await Promise.all(
        (formData || []).map(async (member: any) => ({
          ...member,
          file: await safeBase64ToFile(member.file),
          bodFile: await safeBase64ToFile(member.bodFile),
          citizenshipFront: await safeBase64ToFile(member.citizenshipFront),
          citizenshipBack: await safeBase64ToFile(member.citizenshipBack),
        })),
      );
      setCommitteeTableCooperative(committeeData);
    } catch { /* ignore */ }
  }

  const savedTableExecutiveData = localStorage.getItem("executiveTableCooperative");
  if (savedTableExecutiveData) {
    try {
      const { formData } = JSON.parse(savedTableExecutiveData);
      const executiveData = await Promise.all(
        (formData || []).map(async (member: any) => ({
          ...member,
          file: await safeBase64ToFile(member.file),
          citizenshipFront: await safeBase64ToFile(member.citizenshipFront),
          citizenshipBack: await safeBase64ToFile(member.citizenshipBack),
        })),
      );
      setExecutiveTableCooperative(executiveData);
    } catch { /* ignore */ }
  }

  // Step 4 – Share Member
  const savedShareMemberData = localStorage.getItem("shareMember");
  if (savedShareMemberData) {
    try {
      const { formData } = JSON.parse(savedShareMemberData);
      setShareMember({
        ...formData,
        shareMemberFile: await safeBase64ToFile(formData?.shareMemberFile),
        savingMemberFile: await safeBase64ToFile(formData?.savingMemberFile),
        loanMemberFile: await safeBase64ToFile(formData?.loanMemberFile),
      });
    } catch { /* ignore */ }
  }

  // Step 5 – Documents
  const savedDocumentsData = localStorage.getItem("documents");
  if (savedDocumentsData) {
    try {
      const { formData } = JSON.parse(savedDocumentsData);
      const serializedOthers =
        formData?.otherDocumentFiles ||
        (formData?.otherDocumentFile ? [formData?.otherDocumentFile] : []);
      const loadedOtherFiles: File[] = [];
      for (const item of serializedOthers) {
        const f = await safeBase64ToFile(item);
        if (f) loadedOtherFiles.push(f);
      }

      setDocuments({
        ...formData,
        regCertificateFile: await safeBase64ToFile(formData?.regCertificateFile),
        lawDocumentFile: await safeBase64ToFile(formData?.lawDocumentFile),
        foundingDocumentFile: await safeBase64ToFile(formData?.foundingDocumentFile),
        panDocumentFile: await safeBase64ToFile(formData?.panDocumentFile),
        meetingDocumentFile: await safeBase64ToFile(formData?.meetingDocumentFile),
        businessRegistrationFile: await safeBase64ToFile(formData?.businessRegistrationFile),
        taxClearanceFile: await safeBase64ToFile(formData?.taxClearanceFile),
        agmDocumentFile: await safeBase64ToFile(formData?.agmDocumentFile),
        otherDocumentFiles: loadedOtherFiles,
        otherDocumentFileNames: loadedOtherFiles.map((f) => f.name),
        otherDocumentFile: loadedOtherFiles[0] || null,
        otherDocumentFileName: loadedOtherFiles[0]?.name || "",
      });
    } catch { /* ignore */ }
  }

  // Step 6 – Declaration
  const savedDeclarationData = localStorage.getItem("declaration");
  if (savedDeclarationData) {
    try {
      const { formData } = JSON.parse(savedDeclarationData);
      setDeclaration({ ...formData });
    } catch { /* ignore */ }
  }
};

export const clearRegisterLocalStorage = async () => {
  const keys = [
    "cooperative",
    "address",
    "shareFinancial",
    "loanFinancial",
    "incomeFinancial",
    "auditFinancial",
    "taxFinancial",
    "committeeCooperative",
    "executiveCooperative",
    "committeeTableCooperative",
    "executiveTableCooperative",
    "shareMember",
    "documents",
    "declaration",
  ];

  keys.forEach((key) => localStorage.removeItem(key));
};
